"use client";

import { useHistory, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button-tw";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Plus } from "lucide-react";
import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import { hasPermission } from "@/utils/auth";
import { useAuth } from "@/providers/auth";

import TablePagination from "@/components/table-pagination";
import MainTable from "@/components/main-table";
import TableColumnsManager from "@/components/table-columns-manager";
import SearchInput from "@/components/search-input";
import { useContactDrawer } from "@/hooks/contact/use-contact-drawer";
import { useContacts } from "@/hooks/contact/use-contacts";
import { Badge } from "@/components/ui/badge";
import DeleteContactAlert from "./delete-contact-alert";
import { useContactInvoices } from "@/hooks/invoice/use-contact-invoices";
import { CreateContactDrawer } from "@/components/contact/create-contact-drawer";
import { EditContactDrawer } from "@/components/contact/edit-contact-drawer";
import {
  PAID_INVOICE_STATUS,
  PENDING_INVOICE_STATUS,
  PROCESSING_INVOICE_STATUS,
} from "@/constants/invoice";

// Custom filter function for multi-column searching
const multiColumnFilterFn = (row, _columnId, filterValue) => {
  const searchableRowContent = `${
    row.original.contact_info?.first_name || ""
  } ${row.original.contact_info?.last_name || ""} ${
    row.original.contact_info?.email
  } ${row.original.contact_info?.business_name || ""} ${
    row.original.contact_info?.business_dba || ""
  } ${row.original.contact_info?.contact_ref_id || ""}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const columns = [
  {
    id: "name",
    header: () => <span>Primary contact name</span>,
    accessorFn: (row) =>
      row.contact_info.first_name
        ? `${row.contact_info.first_name} ${
            row.contact_info.last_name || ""
          }`.trim()
        : "",
    size: 340,
    filterFn: multiColumnFilterFn,
    enableHiding: false,
  },
  {
    id: "businessName",
    header: "Business name",
    accessorFn: (row) =>
      row.contact_info?.business_dba || row.contact_info.business_name || "",
    // size: 220,
  },
  {
    id: "email",
    header: "Email",
    accessorFn: (row) => row.contact_info?.email,
    // size: 220,
  },
  {
    id: "refId",
    header: "Reference ID",
    accessorFn: (row) => row.contact_info?.contact_ref_id,
    cell: ({ row }) =>
      row.getValue("refId") ? (
        <div className="">{`#${row.getValue("refId")}`}</div>
      ) : null,
  },
  {
    id: "tags",
    header: "Tags",
    accessorKey: "contact_info.tags",
    cell: ({ row }) => (
      <div className="flex gap-2 flex-wrap max-w-[200px]">
        {row.getValue("tags").map((tag) => (
          <Badge variant="secondary">{tag}</Badge>
        ))}
      </div>
    ),
    // size: 270,
    enableSorting: false,
  },
  {
    id: "created",
    header: "Date created",
    accessorFn: (row) => {
      return row.created_at ? dayjs(row.created_at).format("MM/DD/YYYY") : "";
    },
    // size: 170,
    // filterFn: dateCreatedFilterFn,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.created_at;
      const b = rowB.original.created_at;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
];

const getColumns = (onEditContact) => [
  ...columns,
  {
    id: "@/actions/",
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <RowActions row={row} onEdit={onEditContact} />
      </div>
    ),
    enableHiding: false,
  },
];

const fallbackData = [];

export default function ContactTable() {
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const { data, isLoading } = useContacts();
  const { handleOpen: openContactDrawer } = useContactDrawer();
  const tableRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    email: false,
    created: false,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data: data || fallbackData,
    columns: getColumns(setEditContact),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  return (
    <div className="space-y-4 mb-96">
      {/* Filters */}
      <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 flex-col-reverse lg:flex-row">
        <div className="flex items-center gap-3 grow flex-col lg:flex-row w-full">
          {/* Filter by name or email */}
          <SearchInput
            searchValue={table.getColumn("name")?.getFilterValue()}
            setSearchValue={table.getColumn("name")?.setFilterValue}
          />
          <div className="grow flex justify-between w-full lg:w-auto">
            <div className="flex items-center gap-3 mr-3">
              {/* Toggle columns visibility */}
              <TableColumnsManager table={table} />
            </div>
            {/* <TableActions table={table} /> */}
          </div>
        </div>

        <Button
          className="w-full bg-green-600 hover:bg-green-600/80 text-white lg:w-48"
          onClick={() => setCreateContactOpen(true)}
        >
          <Plus
            className="-ms-1 me-2 "
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
          Create contact
        </Button>
      </div>
      <MainTable
        ref={tableRef}
        table={table}
        isLoading={isLoading}
        onRowClick={(contact) => openContactDrawer(contact.contact_id)}
      />
      <TablePagination table={table} />
      <CreateContactDrawer
        open={createContactOpen}
        handleClose={() => setCreateContactOpen(false)}
      />
      <EditContactDrawer
        open={!!editContact}
        handleClose={() => setEditContact(null)}
        contact={editContact}
      />
      <DeleteContactAlert />
    </div>
  );
}

function RowActions({ row, onEdit }) {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const contact = row.original;

  const { data: contactInvoices, isLoading: invoicesLoading } = useContactInvoices({
    contactId: contact.contact_id,
    enabled: !!contact.contact_id,
  });

  const canDelete = React.useMemo(() => {
    if (invoicesLoading) return false;
    if (!contactInvoices) return true;

    return !contactInvoices.some((invoice) =>
      [
        PAID_INVOICE_STATUS,
        PENDING_INVOICE_STATUS,
        PROCESSING_INVOICE_STATUS,
      ].includes(invoice?.invoice_info?.invoice_status)
    );
  }, [contactInvoices, invoicesLoading]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex justify-end">
            <Button
              size="icon"
              variant="ghost"
              className="shadow-none"
              aria-label="Edit item"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-ellipsis"
              >
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </Button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!hasPermission(user, "contact", "update", contact) && !canDelete && !contact?.contact_info?.email && (
            <div className="text-sm text-slate-700 p-2">No actions</div>
          )}
          <DropdownMenuGroup>
            {hasPermission(user, "contact", "update", contact) && (
              <DropdownMenuItem onClick={() => onEdit(contact)}>
                <span>Edit</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={!contact?.contact_info?.email}
              onClick={() => {
                if (contact?.contact_info?.email) {
                  history.push(`/invoices/new?contact_id=${contact?.contact_id}`);
                }
              }}
            >
              <span>Create invoice</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          {canDelete && (
            <>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => {
                  if (queryParams.has("delete_contact")) {
                    queryParams.delete("delete_contact");
                    history.replace({
                      search: queryParams.toString(),
                    });
                  } else {
                    queryParams.set("delete_contact", contact?.contact_id);
                    history.replace({
                      search: queryParams.toString(),
                    });
                  }
                }}
              >
                <span>Delete</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
